import { waitFor } from "xstate"

import { config } from "./config"

import { authMachine } from "../machines/Authmachine"

import qs from "qs"
import type { FetcherWithComponents } from "react-router"

export async function fetchAPI<T>(
  path: string,
  init: Omit<RequestInit, "body" | "headers"> & { body?: RequestInit["body"] | Record<string, unknown> } = {},
) {
  let retries = 0

  while (true) {
    await waitFor(authMachine, (snapshot) => snapshot.hasTag("Settled"))

    const token = authMachine.getSnapshot().context.token

    if (token == null) {
      authMachine.send({ type: "EXTERNAL_LOGIN" })
      continue
    }

    const res = await fetch(`${path.startsWith("http") ? "" : config.apiUrl}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token.access_token}`,
        "Content-Type": "application/json",
      },
      body: typeof init.body === "object" ? JSON.stringify(init.body) : init.body,
    })

    if (res.status === 401 && ++retries <= 1) {
      authMachine.send({ type: "REFRESH_TOKEN" })
      continue
    }

    return res as CustomResponse<T>
  }
}

export async function handleResponse<T>(response: CustomResponse<T> | Response) {
  if (!response?.ok) throw response
  return (await response.json()) as NonNullable<T>
}

export async function throwIfNotOk<T>(response: CustomResponse<T> | Response) {
  if (response.ok) return

  if (response.status === 304) throw response

  const body: unknown = await response.json().catch(() => null)
  const message = getFirstApiErrorMessage(body)

  throw new ApiRequestError(message ?? "Request failed", response.status, body)
}

export class ApiRequestError extends Error {
  status: number
  body: unknown

  constructor(message: string, status: number, body?: unknown) {
    super(message)
    this.name = "ApiRequestError"
    this.status = status
    this.body = body
    Object.setPrototypeOf(this, ApiRequestError.prototype) 
  }
}

function isApiError(value: unknown): value is APIError {
  return typeof value === "object" && value != null && "errorMessage" in value && typeof value.errorMessage === "string"
}

function getFirstApiErrorMessage(body: unknown): string | undefined {
  if (!Array.isArray(body)) return undefined

  const [first] = body as unknown[]
  return isApiError(first) ? first.errorMessage : undefined
}

export function reactHookFormSubmitShim(
  data: Record<string, unknown>,
  submit: FetcherWithComponents<unknown>["submit"],
) {
  void submit(qs.stringify(data), { method: "POST" })
}

interface CustomResponse<T> {
  json: () => Promise<T>
  status: number
  ok: boolean
}

export interface APIError {
  propertyName: string
  errorMessage: string
  attemptedValue: string
  customState: null
  severity: string
  errorCode: string
  formattedMessagePlaceholderValues: Record<string, string | number>
}
